/**
 * Coco cart custom elements: coco-cart-list, coco-cart-remove, coco-cart-remove-link, coco-cart-note.
 * Event source: 'coco-cart-list'
 */
import { debounce, cocoFetchInit } from '@theme/coco-utilities';
import { onCocoEvent, emitCocoEvent, COCO_EVENTS, DEBOUNCE_MS } from '@theme/theme-events';
import { syncShippingBarFromSource } from '@theme/cart-shipping-progress';

/** Sources that already call cart.refreshFromResponse() after publishing cartUpdate. */
const RENDER_CONTENTS_SOURCES = new Set(['coco-atc', 'product-bundle']);

/** Prevent nested/duplicate applyCartState during add-to-cart. */
let applyingCartState = false;

function focusElement(element) {
  if (element && typeof element.focus === 'function') {
    try {
      element.focus();
    } catch (_error) {
      /* ignore focus errors */
    }
  }
}

class CocoCartRemoveButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const cartItems = this.closest('coco-cart-list');
      cartItems?.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

class CocoCartRemoveLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const cartItems = this.closest('coco-cart-list');
      cartItems?.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

if (!customElements.get('coco-cart-remove')) {
  customElements.define('coco-cart-remove', CocoCartRemoveButton, { extends: 'button' });
}

if (!customElements.get('coco-cart-remove-link')) {
  customElements.define('coco-cart-remove-link', CocoCartRemoveLink, { extends: 'a' });
}

export class CocoCartList extends HTMLElement {
  constructor() {
    super();

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, DEBOUNCE_MS);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = onCocoEvent(COCO_EVENTS.cartUpdated, (event) => {
      if (event.source === 'coco-cart-list') return;
      // coco-atc / product-bundle call refreshFromResponse themselves — skip to avoid double DOM work that freezes the browser
      if (RENDER_CONTENTS_SOURCES.has(event.source)) return;
      return this.onCartUpdate(event);
    });

    this.addEventListener('click', (event) => {
      const removeTrigger = event.target.closest(
        '[is="coco-cart-remove-link"], [is="coco-cart-remove"], [data-cart-remove], .product-giftcard-remove',
      );
      if (removeTrigger && this.contains(removeTrigger)) {
        event.preventDefault();
        if (removeTrigger.getAttribute('aria-disabled') === 'true') return;
        if (removeTrigger.matches('[is="coco-cart-remove-link"], [is="coco-cart-remove"]')) return;
        this.updateQuantity(removeTrigger.dataset.index, 0, event);
        return;
      }

      const button = event.target.closest('[data-cart-qty-adjust]');
      if (!button) return;

      // coco-qty handles +/- inside the web component and dispatches change.
      if (button.closest('coco-qty')) return;

      const qtyWrap = button.closest('.cart-card-qty');
      const input = qtyWrap?.querySelector('.cart-card-qty-input');
      if (!input || input.disabled) return;

      const adjust = parseInt(button.dataset.cartQtyAdjust, 10);
      const currentValue = parseInt(input.value, 10) || 0;
      const min = parseInt(input.dataset.min, 10) || 1;
      const max = input.max ? parseInt(input.max, 10) : Number.MAX_SAFE_INTEGER;
      const nextValue = currentValue + adjust;

      if (nextValue < min) {
        this.setInputValidity(input, window.Coco.qtyStrings.minError.replace('[min]', input.dataset.min));
        return;
      }

      if (nextValue > max) {
        this.setInputValidity(input, window.Coco.qtyStrings.maxError.replace('[max]', input.max));
        return;
      }

      input.value = String(nextValue);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

  }

  isDrawerContext() {
    return Boolean(this.closest('coco-cart-drawer'));
  }

  isCartPageContext() {
    return Boolean(this.closest('[data-cart-container]'));
  }

  getPageCartItemsElement() {
    return document.querySelector('[data-cart-container] coco-cart-list, [data-cart-container] #coco-cart-items');
  }

  getPageSectionId() {
    return this.getPageCartItemsElement()?.dataset?.id;
  }

  isCartPageEmptyTransition(itemCount) {
    const cartContainer = document.querySelector('[data-cart-container]');
    if (!cartContainer) return false;

    const mainCart = cartContainer.querySelector('.coco-main-cart');
    const wasEmpty = mainCart?.classList.contains('hidden') ?? itemCount === 0;
    return wasEmpty !== (itemCount === 0);
  }

  getDrawerSectionIds() {
    const drawerCartItems = document.querySelector('coco-cart-drawer coco-cart-list');
    if (!drawerCartItems) return [];

    const ids = [
      drawerCartItems.dataset.sectionId,
      drawerCartItems.dataset.id,
      drawerCartItems.dataset.sectionKey,
    ].filter(Boolean);

    return [...new Set(ids)];
  }

  resolveSectionHtml(parsedState, candidateIds) {
    if (!parsedState?.sections) return null;

    for (const id of candidateIds) {
      if (parsedState.sections[id]) return parsedState.sections[id];
    }

    const match = Object.entries(parsedState.sections).find(([key]) => {
      return key.includes('cart-drawer') || /(^|__)cart($|[_-])/i.test(key);
    });
    return match ? match[1] : null;
  }

  getSectionsUrl() {
    if (document.querySelector('[data-cart-container]')) {
      return window.location.pathname;
    }

    return window.Coco.routes?.root || '/';
  }

  getItemCount(parsedState) {
    if (parsedState?.item_count != null && parsedState.item_count !== '') {
      const count = Number(parsedState.item_count);
      if (!Number.isNaN(count)) return count;
    }

    if (Array.isArray(parsedState?.items)) return parsedState.items.length;

    // /cart/add.js returns a line item (no item_count). Prefer count from bundled section HTML.
    if (parsedState?.sections) {
      const sectionHtml =
        this.resolveSectionHtml(parsedState, this.getDrawerSectionIds()) ||
        Object.values(parsedState.sections).find(Boolean);
      if (sectionHtml) {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const countEl = doc.querySelector('[data-cart-count]');
        if (countEl) {
          const fromBadge = Number(countEl.textContent.trim());
          if (!Number.isNaN(fromBadge)) return fromBadge;
        }
        const sectionItems = doc.querySelectorAll('.cart-item').length;
        if (sectionItems > 0) return sectionItems;
      }
    }

    const pageLineItems = document.querySelectorAll('[data-cart-container] .cart-item');
    if (pageLineItems.length > 0) return pageLineItems.length;

    const drawerLineItems = document.querySelectorAll('coco-cart-drawer .cart-item');
    if (drawerLineItems.length > 0) return drawerLineItems.length;

    return 0;
  }

  finalizeCartRender(parsedState) {
    let itemCount = this.getItemCount(parsedState);

    if (
      itemCount === 0 &&
      this.isDrawerContext() &&
      document.querySelector('coco-cart-drawer .cart-item')
    ) {
      itemCount = document.querySelectorAll('coco-cart-drawer .cart-item').length;
    }

    this.applyEmptyStateClasses(itemCount);
    document.querySelector('coco-cart-drawer')?.updateCartCount?.(itemCount);
    this.updateCartPageState(itemCount);

    if (itemCount === 0) {
      document.querySelector('coco-cart-drawer')?.removeAttribute('data-last-shipping-progress');
      document.querySelector('[data-cart-container]')?.removeAttribute('data-last-shipping-progress');
    }

    return itemCount;
  }

  updateCartPageState(count) {
    const paddedCount = count < 10 ? `0${count}` : String(count);

    document.querySelectorAll('[data-cart-page-count]').forEach((element) => {
      element.textContent = paddedCount;
      element.classList.toggle('hidden', count === 0);
    });

    const template = window.Coco.cartStrings?.itemsInBasket;
    const itemsInBasketText = template
      ? template.replace(/\{\{\s*count\s*\}\}/g, count).replace('[count]', count)
      : null;

    document.querySelectorAll('[data-cart-count-text], [data-cart-page-meta]').forEach((element) => {
      element.classList.toggle('hidden', count === 0);
      if (itemsInBasketText) element.textContent = itemsInBasketText;
    });
  }

  isDrawerEmptyTransition(itemCount) {
    const drawer = document.querySelector('coco-cart-drawer');
    if (!drawer) return false;

    const wasEmpty = drawer.classList.contains('is-empty');
    const isEmpty = itemCount === 0;
    return wasEmpty !== isEmpty;
  }

  getLineItem(line) {
    return (
      this.querySelector(`.product-giftcard[data-line="${line}"]`) ||
      this.querySelector(`.product-giftcard#coco-cart-item-${line}`) ||
      this.querySelector(`li.cart-item[data-line="${line}"]`) ||
      this.querySelector(`#coco-cart-item-${line}`)
    );
  }

  getRemoveTrigger(lineItem) {
    if (!lineItem) return null;

    if (lineItem.matches('[is="coco-cart-remove-link"], [is="coco-cart-remove"], [data-cart-remove]')) {
      return lineItem;
    }

    return lineItem.querySelector(
      '[is="coco-cart-remove-link"], [is="coco-cart-remove"], [data-cart-remove], .product-giftcard-remove',
    );
  }

  setRemoveButtonLoading(removeTrigger, isLoading) {
    if (!removeTrigger) return;

    removeTrigger.toggleAttribute('aria-disabled', isLoading);

    removeTrigger.querySelectorAll(':scope > *:not([data-loading-spinner])').forEach((element) => {
      element.classList.toggle('hidden', isLoading);
    });

    removeTrigger.querySelectorAll('[data-loading-spinner]').forEach((spinner) => {
      spinner.classList.toggle('hidden', !isLoading);
    });
  }

  getQuantityInput(line) {
    return (
      this.querySelector(`#Quantity-input-${line}`) ||
      this.querySelector(`#Quantity-${line}`) ||
      this.querySelector(`.cart-item[data-line="${line}"] .quantity-input`) ||
      this.querySelector(`li.cart-item:nth-child(${line}) .quantity-input`)
    );
  }

  getCartErrorMessage(parsedState) {
    if (parsedState.errors) return parsedState.errors;
    if (!parsedState.items && (parsedState.description || parsedState.message)) {
      return parsedState.description || parsedState.message;
    }
    if (parsedState.status === 422 || parsedState.status === 'cart_error') {
      return parsedState.description || parsedState.message || window.Coco.cartStrings.error;
    }
    return '';
  }

  setInputValidity(input, message) {
    if (!input) return;

    const wasDisabled = input.disabled;
    if (wasDisabled) input.disabled = false;

    input.setCustomValidity(message);
    focusElement(input);
    input.reportValidity();

    if (input.defaultValue !== '') {
      input.value = input.defaultValue;
    }
    input.select();
  }

  clearInputValidity(input) {
    if (!input) return;

    const wasDisabled = input.disabled;
    if (wasDisabled) input.disabled = false;

    input.setCustomValidity('');
    input.reportValidity();

    if (wasDisabled) input.disabled = true;
  }

  validateQuantity(event) {
    const input = event.target;
    const inputValue = parseInt(input.value, 10);
    const index = input.dataset.index;
    let message = '';

    if (Number.isNaN(inputValue)) {
      message = window.Coco.cartStrings.error;
    } else if (inputValue < parseInt(input.dataset.min, 10)) {
      message = window.Coco.qtyStrings.minError.replace('[min]', input.dataset.min);
    } else if (input.max && inputValue > parseInt(input.max, 10)) {
      message = window.Coco.qtyStrings.maxError.replace('[max]', input.max);
    } else if (inputValue % parseInt(input.step, 10) !== 0) {
      message = window.Coco.qtyStrings.stepError.replace('[step]', input.step);
    }

    if (message) {
      this.setInputValidity(input, message);
      return;
    }

    this.clearInputValidity(input);
    this.updateQuantity(
      index,
      inputValue,
      event,
      document.activeElement?.getAttribute('name'),
      input.dataset.quantityVariantId,
    );
  }

  onChange(event) {
    if (!event.target?.matches?.('.cart-card-qty-input, .quantity-input, .quantity-input')) return;
    this.validateQuantity(event);
  }

  onCartUpdate(cartUpdateEvent) {
    const cartData = cartUpdateEvent.cartData || cartUpdateEvent.cart;

    if (cartData?.errors) {
      this.onCartError(cartData.errors, cartUpdateEvent.target);
      return;
    }

    if (cartData?.sections) {
      this.applyCartState(cartData);
      return;
    }

    return this.fetchAndRefreshSections(cartData);
    
  }

  fetchAndRefreshSections(cartData) {
    const sectionId = this.isDrawerContext()
      ? this.getDrawerSectionIds()[0]
      : this.getPageSectionId() || document.querySelector('[data-cart-container] #coco-cart-items')?.dataset?.id;

    if (!sectionId) return Promise.resolve();

    const sectionsUrl = this.isDrawerContext() ? window.Coco.routes?.root || '/' : window.location.pathname;

    return fetch(`${sectionsUrl}?section_id=${encodeURIComponent(sectionId)}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (this.isDrawerContext()) {
          this.refreshDrawerFromHtml(html);
        } else {
          this.refreshFromSectionHtml(html);
        }

        this.finalizeCartRender(cartData);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  getSourceDrawer(html) {
    return (
      html.querySelector?.('coco-cart-drawer') ||
      html.querySelector?.('[data-cart-drawer-host] coco-cart-drawer') ||
      html.querySelector?.('.shopify-section coco-cart-drawer')
    );
  }

  getDrawerCartItemsElement() {
    return document.querySelector(
      'coco-cart-drawer [data-cart-drawer-items], coco-cart-drawer coco-cart-list, coco-cart-drawer #coco-cart-drawer-items',
    );
  }

  syncDrawerRegionFromSource(sourceElement, targetElement) {
    if (!sourceElement || !targetElement) return;

    targetElement.innerHTML = sourceElement.innerHTML;
    targetElement.className = sourceElement.className;

    if (sourceElement.hasAttribute('hidden')) {
      targetElement.setAttribute('hidden', '');
    } else {
      targetElement.removeAttribute('hidden');
    }
  }

  syncCartDrawerRecommendations(sourceFooter, targetFooter, drawer) {
    if (!sourceFooter || !targetFooter) return;

    const cartDrawer = drawer || document.querySelector('coco-cart-drawer');
    const sourceRec = sourceFooter.querySelector('[data-cart-drawer-recommendations-region], [data-cart-recommendations-region]');
    let targetRec = targetFooter.querySelector('[data-cart-drawer-recommendations-region], [data-cart-recommendations-region]');

    if (!sourceRec) {
      targetRec?.remove();
      return;
    }

    if (!targetRec) {
      const footerBody = targetFooter.querySelector('[data-cart-drawer-footer-body]');
      targetRec = sourceRec.cloneNode(true);
      targetFooter.insertBefore(targetRec, footerBody || null);
    } else {
      this.syncDrawerRegionFromSource(sourceRec, targetRec);
    }

    // Defer recommendations so add-to-cart + drawer open stay responsive
    requestAnimationFrame(() => {
      this.loadCartRecommendations(cartDrawer, false);
      this.reinitCartRecommendationSliders(cartDrawer);
    });
  }

  syncCartPageRecommendations(html) {
    const cartPage = document.querySelector('[data-cart-container]');
    if (!cartPage) return;

    const sourceRec =
      html.querySelector?.('[data-cart-recommendations-region]') ||
      html.querySelector?.('[data-cart-container] [data-cart-recommendations-region]');
    let targetRec = cartPage.querySelector('[data-cart-recommendations-region]');

    if (!sourceRec) {
      targetRec?.remove();
      return;
    }

    if (!targetRec) {
      targetRec = sourceRec.cloneNode(true);
      cartPage.appendChild(targetRec);
    } else {
      this.syncDrawerRegionFromSource(sourceRec, targetRec);
    }

    this.loadCartRecommendations(cartPage, false);
    this.reinitCartRecommendationSliders(cartPage);
  }

  syncCartPageRecommendationsFromState(parsedState) {
    const pageSectionId = this.getPageSectionId();
    if (!pageSectionId || !parsedState?.sections) return;

    const sectionHtml = this.resolveSectionHtml(parsedState, [pageSectionId]);
    if (!sectionHtml) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const sourceItems = doc.querySelector('[data-cart-container] coco-cart-list, coco-cart-list');
    const pageItems = document.querySelector('[data-cart-container] coco-cart-list');

    if (sourceItems && pageItems) {
      if (sourceItems.dataset.firstProductId) {
        pageItems.dataset.firstProductId = sourceItems.dataset.firstProductId;
      } else {
        delete pageItems.dataset.firstProductId;
      }
    }

    this.syncCartPageRecommendations(doc);
  }

  loadCartRecommendations(root, force = false) {
    if (!root) return;

    root.querySelectorAll('coco-recs[data-url]').forEach((element) => {
      if (!force && element.querySelector('.swiper-slide')) return;

      element.reloadRecommendations?.() ?? element.loadRecommendations?.();
    });
  }

  loadCartDrawerRecommendations(drawer, force = false) {
    this.loadCartRecommendations(drawer, force);
  }

  reinitCartRecommendationSliders(root) {
    if (!root) return;

    root.querySelectorAll('[data-cart-drawer-recommendations-region] component-slider, [data-cart-recommendations-region] component-slider').forEach((componentSlider) => {
      if (typeof componentSlider.cleanupSlider === 'function') {
        componentSlider.cleanupSlider();
      } else if (typeof componentSlider.destroySlider === 'function') {
        componentSlider.destroySlider();
      }

      componentSlider.sliderInstance = null;
      componentSlider.handleSlider?.();
    });
  }

  reinitCartDrawerRecommendationSliders(drawer) {
    this.reinitCartRecommendationSliders(drawer);
  }

  refreshDrawerFromHtml(html) {
    const drawer = document.querySelector('coco-cart-drawer');
    if (!drawer) return;

    const sourceDrawer = this.getSourceDrawer(html);
    if (!sourceDrawer) return;

    drawer.syncEmptyStateFromHtml?.(html);

    const sourceCartItems = sourceDrawer.querySelector('coco-cart-list');
    const targetCartItems = drawer.querySelector('coco-cart-list');

    // Keep the live <coco-cart-list> element — replacing the wrapper destroys it and re-subscribes mid-update
    if (sourceCartItems && targetCartItems) {
      targetCartItems.innerHTML = sourceCartItems.innerHTML;

      if (sourceCartItems.dataset.firstProductId) {
        targetCartItems.dataset.firstProductId = sourceCartItems.dataset.firstProductId;
      } else {
        delete targetCartItems.dataset.firstProductId;
      }
    } else {
      const sourceItems = sourceDrawer.querySelector('[data-cart-drawer-items], coco-cart-list');
      const targetItems = drawer.querySelector('[data-cart-drawer-items], coco-cart-list');
      this.syncDrawerRegionFromSource(sourceItems, targetItems);
    }

    const sourceFooter = sourceDrawer.querySelector('[data-cart-drawer-footer], cart-footer');
    const targetFooter = drawer.querySelector('[data-cart-drawer-footer], cart-footer');
    const sourceFooterBody = sourceFooter?.querySelector('[data-cart-drawer-footer-body]');
    const targetFooterBody = targetFooter?.querySelector('[data-cart-drawer-footer-body]');

    if (sourceFooterBody && targetFooterBody) {
      this.syncDrawerRegionFromSource(sourceFooterBody, targetFooterBody);
    } else {
      this.syncDrawerRegionFromSource(sourceFooter, targetFooter);
    }

    this.syncCartDrawerRecommendations(sourceFooter, targetFooter, drawer);

    const sourceShipping = sourceDrawer.querySelector('[data-cart-drawer-shipping]');
    if (sourceShipping) {
      syncShippingBarFromSource(drawer, sourceShipping);
    }

    drawer.setEmptyState?.(sourceDrawer.classList.contains('is-empty'));
  }

  refreshFromSectionHtml(html) {
    if (this.isDrawerContext()) {
      this.refreshDrawerFromHtml(html);
      return;
    }

    const cartPage = document.querySelector('[data-cart-container]');

    this.updateRegionFromHtml(
      html,
      '[data-cart-container] [data-cart-items-list]',
      '[data-cart-container] [data-cart-items-list], coco-cart-list [data-cart-items-list]',
    );
    this.updateRegionFromHtml(
      html,
      '[data-cart-container] [data-cart-footer]',
      '[data-cart-container] [data-cart-footer], [data-cart-footer]',
    );

    const sourceShipping = html.querySelector(
      '[data-cart-footer] [data-cart-drawer-shipping], [data-cart-drawer-shipping]',
    );
    if (cartPage && sourceShipping) {
      syncShippingBarFromSource(cartPage, sourceShipping);
    }

    this.syncCartPageRecommendations(html);
  }

  updateRegionFromHtml(html, targetSelector, sourceSelector) {
    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;

    const sourceElement = html.querySelector(sourceSelector);
    if (sourceElement) {
      targetElement.innerHTML = sourceElement.innerHTML;
    } else if (sourceSelector === '[data-cart-drawer-shipping-region]') {
      targetElement.innerHTML = '';
    }
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  renderSections(parsedState) {
    this.getSectionTargets().forEach((section) => {
      if (!section.parseSelector) return;

      const sectionHtml = this.resolveSectionHtml(parsedState, [
        section.section,
        ...(section.fallbackSections || []),
      ]);
      if (!sectionHtml) return;

      const root = document.querySelector(section.rootSelector || section.targetSelector);
      if (!root) return;

      const elementToReplace = section.innerSelector
        ? root.querySelector(section.innerSelector) || root
        : root;

      elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.parseSelector);
    });
  }

  renderDrawerSections(parsedState) {
    const drawerSectionHtml = this.resolveSectionHtml(parsedState, this.getDrawerSectionIds());
    if (!drawerSectionHtml) return false;

    const doc = new DOMParser().parseFromString(drawerSectionHtml, 'text/html');
    this.refreshDrawerFromHtml(doc);
    return true;
  }

  fetchAndRefreshDrawer() {
    const sectionId = this.getDrawerSectionIds()[0];
    if (!sectionId) return Promise.resolve(false);

    const sectionsUrl = window.Coco.routes?.root || '/';

    return fetch(`${sectionsUrl}?section_id=${encodeURIComponent(sectionId)}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.refreshDrawerFromHtml(html);
        return true;
      })
      .catch((error) => {
        console.error(error);
        return false;
      });
  }

  refreshFromResponse(parsedState) {
    this.applyCartState(parsedState);
  }

  applyEmptyStateClasses(itemCount) {
    const isEmpty = itemCount === 0;
    const cartContainer = document.querySelector('[data-cart-container]');

    if (cartContainer) {
      cartContainer.classList.toggle('is-empty', isEmpty);
      cartContainer.querySelector('[data-cart-empty-region]')?.classList.toggle('hidden', !isEmpty);
      cartContainer.querySelector('.coco-main-cart')?.classList.toggle('hidden', isEmpty);
      cartContainer.querySelector('coco-cart-list, #coco-cart-items')?.classList.toggle('hidden', isEmpty);
      cartContainer.querySelector('[data-cart-items-list]')?.classList.toggle('is-empty', isEmpty);
      cartContainer.querySelector('[data-cart-footer]')?.classList.toggle('is-empty', isEmpty);
    }

    cartContainer?.closest('.cart')?.classList.toggle('is-empty', isEmpty);

    document.querySelector('coco-cart-drawer')?.setEmptyState?.(isEmpty);
  }

  pageSectionsRendered(parsedState) {
    const pageSectionId = this.getPageSectionId();
    if (!pageSectionId) return true;

    return Boolean(this.resolveSectionHtml(parsedState, [pageSectionId]));
  }

  applyCartState(parsedState) {
    if (!parsedState || applyingCartState) return;

    applyingCartState = true;

    try {
      const itemCount = this.getItemCount(parsedState);
      const cartDrawerWrapper = document.querySelector('coco-cart-drawer');
      const needsFullDrawerRefresh = cartDrawerWrapper && this.isDrawerEmptyTransition(itemCount);
      const needsCartPageRefresh =
        this.isCartPageContext() && this.isCartPageEmptyTransition(itemCount);

      const finishRender = () => {
        this.renderSections(parsedState);
        this.syncCartPageRecommendationsFromState(parsedState);

        if (this.isCartPageContext() && !this.pageSectionsRendered(parsedState)) {
          this.fetchAndRefreshSections(parsedState);
          return;
        }

        this.finalizeCartRender(parsedState);
      };

      if (needsFullDrawerRefresh) {
        // Prefer bundled sections from cart/add — avoid a second full section fetch that freezes the UI
        if (this.renderDrawerSections(parsedState)) {
          finishRender();
        } else {
          this.fetchAndRefreshDrawer().then(finishRender);
        }
        return;
      }

      if (needsCartPageRefresh) {
        if (cartDrawerWrapper) {
          if (!this.renderDrawerSections(parsedState)) {
            this.fetchAndRefreshDrawer();
          }
        }

        this.fetchAndRefreshSections(parsedState);
        return;
      }

      if (cartDrawerWrapper) {
        const drawerRendered = this.renderDrawerSections(parsedState);
        if (!drawerRendered) {
          this.fetchAndRefreshDrawer().then(finishRender);
          return;
        }
      }

      finishRender();
    } finally {
      applyingCartState = false;
    }
  }

  getSectionTargets() {
    const sections = [];

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        section: 'cart-icon-bubble',
        rootSelector: '#cart-icon-bubble',
        innerSelector: '.shopify-section',
        parseSelector: '.shopify-section',
      });
    }

    const drawerCartItems = document.querySelector('coco-cart-drawer coco-cart-list');
    if (drawerCartItems) {
      this.getDrawerSectionIds().forEach((id) => {
        sections.push({ section: id });
      });
    }

    const pageCartItems = this.getPageCartItemsElement();
    const pageSectionId = pageCartItems?.dataset?.id;
    if (pageSectionId) {
      sections.push(
        {
          section: pageSectionId,
          rootSelector: '[data-cart-container] [data-cart-items-list]',
          parseSelector: 'coco-cart-list [data-cart-items-list], [data-cart-items-list]',
        },
        {
          section: pageSectionId,
          rootSelector: '[data-cart-container] [data-cart-footer]',
          parseSelector: '[data-cart-footer]',
        },
      );
    }

    return sections;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const sections = [
      ...new Set([
        ...this.getSectionTargets().map((section) => section.section),
        ...this.getDrawerSectionIds(),
      ]),
    ];
    const body = JSON.stringify({
      line,
      quantity,
      sections,
      sections_url: this.getSectionsUrl(),
    });
    const requestedQuantity = parseInt(quantity, 10);

    fetch(`${window.Coco.routes.cartChange}`, { ...cocoFetchInit(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        let parsedState;
        try {
          parsedState = JSON.parse(state);
        } catch (_parseError) {
          this.onCartError(state, event.currentTarget, line);
          return;
        }

        try {
          const items = this.querySelectorAll('.cart-item');
          const cartErrorMessage = this.getCartErrorMessage(parsedState);

          if (cartErrorMessage) {
            const quantityElement = this.getQuantityInput(line);
            const errorMessage =
              typeof cartErrorMessage === 'string' ? cartErrorMessage : window.Coco.cartStrings.error;

            this.disableLoading(line);
            this.setInputValidity(quantityElement, errorMessage);
            return;
          }

          this.applyCartState(parsedState);

          const quantityElement = this.getQuantityInput(line);
          const removedLine = requestedQuantity === 0 && parsedState.items.length < items.length;
          let message = '';

          if (!removedLine && quantityElement) {
            const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
            const currentValue = parseInt(quantityElement.value, 10);

            if (items.length === parsedState.items.length && updatedValue !== currentValue) {
              if (typeof updatedValue === 'undefined') {
                message = window.Coco.cartStrings.error;
              } else {
                message = window.Coco.cartStrings.quantityError.replace('[quantity]', updatedValue);
              }
            }
          }

          if (message) {
            this.disableLoading(line);
            this.setInputValidity(quantityElement, message);
          } else {
            this.clearInputValidity(quantityElement);
          }

          const lineItem = this.getLineItem(line);
          const focusTarget = lineItem?.querySelector(`[name="${name}"]`);

          if (message && quantityElement) {
            focusElement(quantityElement);
          } else if (focusTarget) {
            focusElement(focusTarget);
          } else if (parsedState.item_count === 0) {
            focusElement(
              document.querySelector(
                '[data-cart-empty-region] a, [data-cart-drawer-empty-region] a, .cart-empty a',
              ),
            );
          } else if (this.isDrawerContext()) {
            focusElement(this.querySelector('.cart-item-name'));
          }
        } catch (renderError) {
          console.error(renderError);
          this.onCartError(window.Coco.cartStrings.error, event.currentTarget, line);
        }

        emitCocoEvent(COCO_EVENTS.cartUpdated, { source: 'coco-cart-list', cartData: parsedState, variantId: variantId });
      })
      .catch((error) => {
        console.error(error);
        this.onCartError(window.Coco.cartStrings.error, event.currentTarget, line);
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  onCartError(errors, _target, line) {
    const quantityElement = this.getQuantityInput(line);
    const message = typeof errors === 'string' ? errors : window.Coco.cartStrings.error;

    if (line) this.disableLoading(line);
    this.setInputValidity(quantityElement, message);
  }

  enableLoading(line) {
    this.classList.add('cart-items-disabled');
    this.getDrawerCartItemsElement()?.classList.add('cart-items-disabled');

    const lineItem = this.getLineItem(line);
    lineItem?.querySelectorAll('.loading-spinner[data-loading-spinner]').forEach((overlay) => {
      overlay.classList.remove('hidden');
    });
    lineItem?.classList.add('is-loading');

    this.setRemoveButtonLoading(this.getRemoveTrigger(lineItem), true);

    lineItem?.querySelectorAll('.quantity-button, .quantity-input').forEach((control) => {
      control.setAttribute('aria-disabled', 'true');
      if (control.matches('.quantity-input')) control.disabled = true;
    });

    document.activeElement?.blur();

    const lineItemStatus =
      document.getElementById('coco-cart-line-status') || document.getElementById('shopping-cart-line-item-status');
    lineItemStatus?.setAttribute('aria-hidden', 'false');
  }

  disableLoading(line) {
    this.classList.remove('cart-items-disabled');
    this.getDrawerCartItemsElement()?.classList.remove('cart-items-disabled');

    const lineItem = this.getLineItem(line);
    lineItem?.querySelectorAll('.loading-spinner[data-loading-spinner]').forEach((overlay) => {
      overlay.classList.add('hidden');
    });
    lineItem?.classList.remove('is-loading');

    this.setRemoveButtonLoading(this.getRemoveTrigger(lineItem), false);

    lineItem?.querySelectorAll('.quantity-button, .quantity-input').forEach((control) => {
      control.removeAttribute('aria-disabled');
      if (control.matches('.quantity-input')) control.disabled = false;
    });

    const lineItemStatus =
      document.getElementById('coco-cart-line-status') || document.getElementById('shopping-cart-line-item-status');
    lineItemStatus?.setAttribute('aria-hidden', 'true');
  }
}

if (!customElements.get('coco-cart-list')) {
  customElements.define('coco-cart-list', CocoCartList);
}

if (!customElements.get('coco-cart-note')) {
  customElements.define(
    'coco-cart-note',
    class CocoCartNote extends HTMLElement {
      constructor() {
        super();

        if (this.isDrawerContext()) {
          this.addEventListener('click', (event) => this.onApplyClick(event));
          return;
        }

        const textarea = this.getTextarea();
        if (!textarea) return;

        textarea.addEventListener(
          'input',
          debounce((event) => {
            this.saveNote(event.target.value);
          }, DEBOUNCE_MS),
        );
      }

      isDrawerContext() {
        return Boolean(this.closest('coco-cart-drawer'));
      }

      getTextarea() {
        return this.querySelector('textarea[name="note"]');
      }

      getApplyButton() {
        return this.querySelector('[data-cart-note-apply]');
      }

      saveNote(note) {
        const body = JSON.stringify({ note });
        return fetch(`${window.Coco.routes.cartUpdate}`, { ...cocoFetchInit(), ...{ body } });
      }

      onApplyClick(event) {
        const applyButton = event.target.closest('[data-cart-note-apply]');
        if (!applyButton || !this.contains(applyButton)) return;

        event.preventDefault();

        if (applyButton.getAttribute('aria-disabled') === 'true') return;

        const textarea = this.getTextarea();
        if (!textarea) return;

        applyButton.setAttribute('aria-disabled', 'true');

        this.saveNote(textarea.value)
          .then(() => {
            this.closest('.cart-addons-drawer')?.querySelector('[is="close-drawer"]')?.click();
          })
          .finally(() => {
            applyButton.removeAttribute('aria-disabled');
          });
      }
    },
  );
}
